import { Box, HBox, VBox } from "@ui/box/Box"
import { Typo } from "@ui/text/Typo"
import type { ReactNode } from "react"
import { Avatar, DS } from "../ds/Kit"
import { StatusBadge } from "./StatusBadge"

const ROLE_LABELS: Record<string, string> = { teamlead: "Team lead", team_lead: "Team lead", buyer: "Buyer", admin: "Admin" }

const formatRole = (role?: string) => {
	const key = (role || "").toLowerCase()
	return ROLE_LABELS[key] ?? (key ? key.charAt(0).toUpperCase() + key.slice(1) : "Member")
}

const CardAction = ({ tone, onClick, children }: { tone: "accent" | "danger"; onClick: () => void; children: ReactNode }) => (
	<Box
		element='button'
		type='button'
		onClick={(event) => {
			event?.stopPropagation()
			onClick()
		}}
		sx={{
			display: "inline-flex",
			alignItems: "center",
			gap: "6px",
			height: "32px",
			padding: "0 10px",
			border: 0,
			borderRadius: "8px",
			background: "transparent",
			color: tone === "danger" ? "#B91C1C" : DS.accent,
			fontFamily: "inherit",
			fontSize: "13px",
			fontWeight: 600,
			cursor: "pointer",
			"&:hover": { background: tone === "danger" ? "#FEF2F2" : DS.accentSoft },
			"&:focus-visible": { outline: `2px solid ${tone === "danger" ? "#B91C1C" : DS.accent}`, outlineOffset: "1px" }
		}}
	>
		{children}
	</Box>
)

/** One team member: identity, buyer money at a glance, and quiet actions in the footer. */
export const TeamMemberCard = ({ name, email, role, status, joined, stats, onOpen, onTopUp, removeLabel = "Remove", onRemove }: {
	name: string
	email?: string
	role?: string
	status?: string
	joined?: string | null
	stats?: { label: string; value: string }[]
	onOpen: () => void
	onTopUp?: () => void
	removeLabel?: string
	onRemove?: () => void
}) => (
	<VBox
		gap='0'
		role='button'
		tabIndex={0}
		aria-label={`Open member ${name}`}
		onClick={onOpen}
		onKeyDown={(event: { key: string; target: EventTarget; currentTarget: EventTarget }) => {
			if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) onOpen()
		}}
		sx={{
			minWidth: 0,
			borderRadius: DS.radius,
			border: `1px solid ${DS.line}`,
			background: DS.surface,
			cursor: "pointer",
			overflow: "hidden",
			transition: "border-color .15s ease, box-shadow .15s ease, transform .15s ease",
			"&:hover": { borderColor: "#B9CDE2", boxShadow: "0 10px 24px rgba(30, 58, 95, 0.10)", transform: "translateY(-1px)" },
			"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "2px" }
		}}
	>
		<VBox gap='14px' sx={{ padding: "16px 16px 14px" }}>
			<HBox gap='12px' sx={{ alignItems: "center", minWidth: 0 }}>
				<Avatar name={name} size={42} />
				<VBox gap='3px' sx={{ minWidth: 0, flex: 1 }}>
					<Typo sx={{ fontSize: "15px", fontWeight: 650, color: DS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</Typo>
					<Typo sx={{ fontSize: "12px", color: DS.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						<Box element='span' sx={{ color: DS.text, fontWeight: 600 }}>{formatRole(role)}</Box>
						{email ? ` · ${email}` : ""}
					</Typo>
				</VBox>
				{status && <Box sx={{ flexShrink: 0, alignSelf: "flex-start" }}><StatusBadge status={status} /></Box>}
			</HBox>
			{stats && (
				<Box sx={{ display: "grid", gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))`, padding: "10px 12px", borderRadius: "12px", background: DS.soft }}>
					{stats.map((stat, index) => (
						<VBox key={stat.label} gap='3px' sx={{ minWidth: 0, paddingLeft: index === 0 ? 0 : "10px", marginLeft: index === 0 ? 0 : "10px", borderLeft: index === 0 ? undefined : `1px solid ${DS.line}` }}>
							<Typo sx={{ color: DS.muted, fontSize: "11px", whiteSpace: "nowrap" }}>{stat.label}</Typo>
							<Typo sx={{ color: DS.ink, fontSize: "13px", fontWeight: 700, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{stat.value}</Typo>
						</VBox>
					))}
				</Box>
			)}
		</VBox>
		<HBox gap='4px' sx={{ alignItems: "center", minHeight: "44px", padding: "4px 8px 4px 16px", marginTop: "auto", borderTop: `1px solid ${DS.line}`, background: "#FCFCFD" }}>
			<Typo sx={{ flex: 1, fontSize: "12px", color: DS.faint, whiteSpace: "nowrap" }}>{joined ? `Joined ${joined}` : ""}</Typo>
			{onTopUp && (
				<CardAction tone='accent' onClick={onTopUp}>
					<svg aria-hidden width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round'><path d='M12 5v14M5 12h14' /></svg>
					Top Up
				</CardAction>
			)}
			{onRemove && <CardAction tone={removeLabel === "Remove" ? "danger" : "accent"} onClick={onRemove}>{removeLabel}</CardAction>}
		</HBox>
	</VBox>
)
